"use client";

import { useEffect } from "react";

import { isPlatformHost } from "@/lib/platformHost";

// Versi build di-inline saat build (next.config `env`). Dipakai sebagai query
// "?v=" pada scriptURL → deploy baru = scriptURL baru = update SW terdeteksi
// otomatis, dan SW memakai versi ini untuk menamai cache (anti-"lengket").
const SW_VERSION = process.env.NEXT_PUBLIC_SW_VERSION || "dev";

/**
 * Mendaftarkan SATU service worker (`/sw.js`, scope "/") untuk KEDUA PWA
 * (Pelanggan & Admin). Dimount di `Providers` (global) → jalan di storefront
 * maupun `/admin`. Render null — tanpa DOM.
 *
 * Hanya aktif di produksi agar tidak mengganggu hot-reload saat `next dev`.
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    // Origin platform (kelolify.com) TIDAK boleh punya service worker toko.
    // Sebelum halaman platform ada, origin ini menyajikan toko penuh, jadi SW
    // PWA toko sudah terlanjur ter-register di sana dan akan terus menyajikan
    // shell toko dari cache meski server sudah mengirim halaman platform.
    // Service worker hidup PER-ORIGIN, jadi hanya kode yang jalan di origin itu
    // yang bisa mencabutnya — karena itu unregister + buang cache di sini.
    // localhost bukan platform host, jadi dev tidak terpengaruh.
    if (isPlatformHost(window.location.hostname)) {
      void navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
        .catch(() => {});
      if (typeof caches !== "undefined") {
        void caches
          .keys()
          .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
          .catch(() => {});
      }
      return;
    }

    if (process.env.NODE_ENV !== "production") return;
    const register = () => {
      navigator.serviceWorker
        .register(`/sw.js?v=${encodeURIComponent(SW_VERSION)}`, { scope: "/" })
        .catch((err) => {
          // Kegagalan SW tak boleh menggagalkan halaman.
          // eslint-disable-next-line no-console
          console.error("Service worker registration failed:", err);
        });
    };
    if (document.readyState === "complete") {
      register();
      return;
    }
    window.addEventListener("load", register, { once: true });
    return () => window.removeEventListener("load", register);
  }, []);

  return null;
}
